// Campus parking lots, as the /parking map draws them: coloured by who the lot
// is for, labelled with what it costs RIGHT NOW.
//
// The live state is the whole point, and it is the one thing a printed sign
// cannot tell you. Nearly every lot on this campus is two different places
// depending on the hour — a student permit lot is closed to you at 2pm and open
// to anyone at 5pm, a visitor lot charges $2.50 until 4:30 and nothing after —
// so a map that shows the categories alone answers the question wrongly half of
// every weekday. Everything here is therefore evaluated against a clock.
//
// There are no filters, unlike /parking. That page is *about* parking, so it
// can afford controls for "show me what's free at 5pm"; here the lots are
// context around a walking route, and the only question worth answering without
// being asked is "what is this lot doing now".
//
// Pure — no DOM, no fetch — so the schedule arithmetic can be tested under node
// (parking.test.mjs), which is where it would otherwise be least visible and
// most wrong.

// Same palette as /parking's map, so the two pages agree about what a colour
// means. `labelBg` is the tint the lot's own label sits on.
export const LOT_COLORS = {
  accessible: { fill: '#6a1b9a', stroke: '#4a148c', labelBg: 'rgba(226,205,240,0.95)' },
  residence:  { fill: '#a1887f', stroke: '#795548', labelBg: 'rgba(216,199,190,0.95)' },
  visitor:    { fill: '#1565c0', stroke: '#0d47a1', labelBg: 'rgba(197,222,247,0.95)' },
  student:    { fill: '#f57c00', stroke: '#e65100', labelBg: 'rgba(255,222,180,0.95)' },
  staff:      { fill: '#c62828', stroke: '#b71c1c', labelBg: 'rgba(247,203,203,0.95)' },
  dropoff:    { fill: '#00897b', stroke: '#00695c', labelBg: 'rgba(183,234,229,0.95)' },
  default:    { fill: '#d94f4f', stroke: '#d94f4f', labelBg: 'rgba(255,225,180,0.95)' },
};

/** Which palette entry a lot's category means. Order matters — see /parking. */
export function lotColors(category) {
  const cat = String(category || '').toLowerCase();
  if (cat.includes('accessible')) return LOT_COLORS.accessible;
  if (cat.includes('drop')) return LOT_COLORS.dropoff;
  if (cat.includes('residence')) return LOT_COLORS.residence;
  if (cat.includes('visitor') || cat.includes('parkade') || cat.includes('pay-by-plate')) return LOT_COLORS.visitor;
  if (cat.includes('student')) return LOT_COLORS.student;
  if (cat.includes('staff')) return LOT_COLORS.staff;
  return LOT_COLORS.default;
}

const PAID_FROM = 7 * 60 + 30;   // 07:30
const PAID_UNTIL = 16 * 60 + 30; // 16:30

/**
 * Is this lot in its restricted/charged part of the week right now?
 *
 * The same rules /parking runs on, in the device's own clock — every reader of
 * this is standing on the campus the schedule describes, and a lot's hours are
 * posted in local time on the sign beside it.
 *
 *  · `weekday_window` — charged (or permit-only) Mon–Fri 7:30–4:30, free
 *    otherwise. This is nearly every lot.
 *  · `after_hours` — the inverse: free to permit holders in the working day,
 *    paid flat rate evenings and weekends. The N lots.
 *  · `parkade` — always charging.
 */
export function isPaidNow(paidSchedule, now = new Date()) {
  const type = paidSchedule?.type;
  if (!type) return false;
  const day = now.getDay();
  const min = now.getHours() * 60 + now.getMinutes();
  const weekend = day === 0 || day === 6;
  if (type === 'weekday_window') return !weekend && min >= PAID_FROM && min < PAID_UNTIL;
  if (type === 'after_hours') return weekend || min >= PAID_UNTIL || min < PAID_FROM;
  if (type === 'parkade') return true;
  return false;
}

/**
 * What this lot charges, as its own thing: "$2.50".
 *
 * The first line only — some carry two ("$2.25 / ½hr (1st hr)\n$4.50 after"),
 * and the first is the one printed on the sign at the entrance.
 *
 * Deliberately NOT folded into the status below. A price is a standing fact
 * about a lot; the status is what it is doing this minute, and the two answer
 * different questions ("can I park here now" versus "what will it cost me at
 * ten tomorrow"). Keeping them apart is also what lets the price stay on the
 * map at every zoom while the status comes and goes.
 *
 * Accessible and dropoff areas carry no price here even where the file has one:
 * their badge is a requirement, not a tariff, and a rate beside it reads as
 * something anybody can pay to park in.
 */
export function lotRate(lot) {
  const cat = String(lot?.category || '').toLowerCase();
  if (cat.includes('accessible') || cat.includes('drop')) return '';
  const r = String(lot?.hourlyRate || '').split('\n')[0].trim();
  return r && r !== '—' ? r : '';
}

/**
 * The same price with everything but the money stripped: "$2.25 / ½hr (1st hr)"
 * → "$2.25".
 *
 * For the pulled-back label, where the lot's name shrinks to "Q Lot" and the
 * price sits on the same line as it. At campus scale the question a price
 * answers is "which of these do I drive to", and the units and the first-hour
 * rider are three words that make the box wide enough to cover the lot beside
 * it. Zoomed in the whole rate is printed, because by then the question has
 * become "what will this actually cost me".
 */
export function shortRate(rate) {
  const m = String(rate || '').match(/\$\s*\d+(?:[.,]\d{2})?/);
  return m ? m[0].replace(/\s+/g, '') : String(rate || '').trim();
}

/**
 * What this lot is doing at `now`, as one badge: `{ text, tone }` — or **null**
 * when the price alone already says it.
 *
 * That null is the same choice /parking makes and it is worth stating: a
 * visitor lot inside its charging hours needs no "Paid" badge, because the
 * "$2.50" sitting beside it is the statement. A badge as well would be the
 * same fact twice on a label that has to be read at a glance.
 *
 * One badge otherwise, not a summary — it sits on a map behind a route, so it
 * gets the single fact that decides whether somebody can park there. "Open to
 * all" is the one worth the most: a permit lot standing empty at 5pm looks
 * exactly like a permit lot you may not use at 2pm.
 */
export function lotStatus(lot, now = new Date()) {
  const cat = String(lot?.category || '').toLowerCase();
  if (cat.includes('accessible')) return { text: 'Accessibility permit', tone: 'permit' };
  if (cat.includes('drop')) return { text: 'Pickup / dropoff', tone: 'info' };

  const type = lot?.paidSchedule?.type || null;
  const paid = isPaidNow(lot?.paidSchedule, now);
  const permitBased = cat.includes('student') || cat.includes('staff') || cat.includes('carpool');

  // A parkade charges all day every day, so its price is its whole story.
  if (type === 'parkade') return lotRate(lot) ? null : { text: 'Paid', tone: 'paid' };
  // An N lot: the working day belongs to permit holders, and everyone else pays
  // a flat rate in the evening. Both halves are the opposite of every other lot,
  // which is exactly why it needs saying out loud.
  if (type === 'after_hours') {
    return paid
      ? { text: 'Paid — $5 flat', tone: 'paid' }
      : { text: 'Permit required', tone: 'permit' };
  }
  if (permitBased) {
    return paid
      ? { text: 'Permit required', tone: 'permit' }
      // "now", and the word is load-bearing. Without it this reads as a
      // standing fact about the lot — which is exactly the wrong thing to
      // believe about a permit lot at 4:31 on a Tuesday, because it goes back
      // to permit-only at half past seven tomorrow morning. Every other badge
      // here describes the lot; this one describes the hour, and it is the one
      // somebody is most likely to act on.
      : { text: 'Open to all now', tone: 'free' };
  }
  if (paid) return lotRate(lot) ? null : { text: 'Paid', tone: 'paid' };
  return { text: 'Free now', tone: 'free' };
}

/**
 * The same answer as `lotStatus`, but never null — for the lot's own card.
 *
 * On the map the price beside a charging lot already says "paid", so the badge
 * is dropped rather than printed twice (see above). A card is read one lot at a
 * time and is a list of facts about it, so the silence that works on a crowded
 * map reads there as a missing row: it must say what the lot is doing right
 * now, in the same words and the same colour as the badge.
 */
export function lotStatusNow(lot, now = new Date()) {
  return lotStatus(lot, now)
    || (isPaidNow(lot?.paidSchedule, now)
      ? { text: 'Charging now', tone: 'paid' }
      : { text: 'Free now', tone: 'free' });
}

/**
 * When the restricted half of the week runs — the hours BEHIND the badge.
 *
 * The badge says what the lot is doing this minute, which is the only thing
 * worth space on a map. It is also the one fact that will be wrong by the time
 * somebody drives over, so a card that shows it has to show the schedule it
 * came from: "open to all now" means nothing without "…until 7:30 tomorrow".
 */
export function lotHours(paidSchedule) {
  switch (paidSchedule?.type) {
    // Non-breaking spaces inside each time: in the card's narrow value column
    // this wraps, and left to itself it breaks between "4:30" and "PM" — a
    // clock time split across two lines reads as two facts. It may only break
    // at the comma.
    case 'weekday_window': return 'Monday to Friday, 7:30 AM – 4:30 PM';
    case 'after_hours': return 'Evenings, overnight and weekends';
    case 'parkade': return 'Every day, all day';
    default: return '';
  }
}

/**
 * The name on the sign: "ACW – Paid/Visitor" → "ACW".
 *
 * Both dash characters, because the data uses an EN DASH and the obvious
 * hyphen test silently never matches — which is why /parking's collapsed
 * labels still print the whole section name.
 */
export function shortLotName(name) {
  const m = String(name || '').match(/^(.*?)\s*[–—-]\s+/);
  return (m ? m[1] : String(name || '')).trim();
}

/**
 * The name as it is spoken: "Q" → "Q Lot".
 *
 * A single letter on a map is a puzzle — it could be a building, a wing, a
 * legend key. "Q Lot" is what the sign says and what somebody would tell you on
 * the phone, and it costs three characters.
 *
 * Anything that already names its own kind is left alone: "Parkade Lot" is not
 * a thing, and neither is "N Lot Lot", "Dafoe Bike Lockers Lot" or "Pembina
 * Hall Dropoff Lot" — the file keeps bike racks and dropoff bays in the same
 * list as the car lots, and those three came back wrong the moment their names
 * were run through here.
 */
export function lotDisplayName(short) {
  const s = String(short || '').trim();
  if (!s) return '';
  return /\b(lots?|parkade|garage|deck|zone|parking|locker|lockers|bike|bicycle|drop-?off)\b/i.test(s)
    ? s : `${s} Lot`;
}

/**
 * A section's whole name, spoken: "Q – Student Permit" → "Q Lot – Student
 * Permit".
 *
 * The lot letter is expanded in place and the rest of the name is left exactly
 * as authored, so the long form and the short one ("Q Lot") name the same
 * place rather than two. Used for the map label and for the search results,
 * which is the point: a person reading "Q Lot – Student Permit" on the map and
 * typing "Q" into the search box has to be shown the same words back.
 */
export function lotFullName(name) {
  const full = String(name || '').trim();
  const short = shortLotName(full);
  const spoken = lotDisplayName(short);
  return short && full.startsWith(short) ? spoken + full.slice(short.length) : full;
}

/** Mean of a ring's vertices — good enough to hang a label on. */
function centroid(ring) {
  let lat = 0, lng = 0;
  for (const p of ring) { lat += p.lat; lng += p.lng; }
  return ring.length ? { lat: lat / ring.length, lng: lng / ring.length } : null;
}

/**
 * The authored file (lots → sections) flattened to the priced areas that get
 * drawn. Mirrors `api/_parkingData.js`'s `flattenLots`, including the rule that
 * a section inherits its lot's schedule — the file is grouped that way because
 * a lot is paid or free as one thing — and tolerating an already-flat list,
 * since this format has changed under its readers once already.
 */
function flatten(raw) {
  if (!Array.isArray(raw)) return [];
  if (raw.some(e => e && !Array.isArray(e.sections))) return raw;
  const out = [];
  for (const lot of raw) {
    for (const sec of (lot?.sections || [])) {
      if (!sec || typeof sec !== 'object') continue;
      out.push({
        ...sec,
        lot: lot.lot ?? null,
        name: String(sec.name || '').trim() || String(lot.lot || '').trim(),
        paidSchedule: sec.paidSchedule ?? lot.paidSchedule ?? null,
      });
    }
  }
  return out;
}

/**
 * Everything needed to draw the lots: `[{ id, name, short, colors, ring,
 * position, status }]`.
 *
 * Two kinds of lot are left out, matching what /parking shows by default.
 * RESIDENCE lots are for people who already live in the building beside them,
 * so they answer nobody's question here; PERMIT-ONLY lots are ones no visitor
 * can ever use at any hour, and a shape you may never park in is just a
 * coloured patch over the campus. Anything without a drawn polygon is skipped
 * for the obvious reason — 17 of the 63 priced areas have one.
 */
export function parkingLots(raw, now = new Date()) {
  const out = [];
  for (const lot of flatten(raw)) {
    const cat = String(lot?.category || '').toLowerCase();
    if (cat.includes('residence')) continue;
    if (lot?.paidSchedule?.type === 'permit_only') continue;
    const ring = (lot?.polygon || [])
      .map(c => ({ lat: Number(c?.latitude ?? c?.lat), lng: Number(c?.longitude ?? c?.lng) }))
      .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (ring.length < 3) continue;
    const position = centroid(ring);
    if (!position) continue;
    const full = String(lot.name || '').trim();
    out.push({
      id: `${lot.lot || ''}::${full}`,
      // "Q – Paid/Visitor" → "Q Lot – Paid/Visitor", so the long form and the
      // short one name the same place rather than two. Shared with the search
      // entries (graph.js), which have to say the same words back.
      name: lotFullName(full),
      short: lotDisplayName(shortLotName(full)),
      category: lot.category || '',
      colors: lotColors(lot.category),
      ring,
      position,
      status: lotStatus(lot, now),
      // The map's badge (null when the price says it) and the card's, which
      // always says something. Both re-derived on the same minute tick.
      statusNow: lotStatusNow(lot, now),
      hours: lotHours(lot.paidSchedule),
      rate: lotRate(lot),
      // The same price twice: the whole tariff for the close-in label and the
      // sheet, the money alone for the pulled-back one.
      rateShort: shortRate(lotRate(lot)),
      /**
       * The HonkMobile zone and its deep link, where the lot has one.
       *
       * Carried rather than derived because the zone number is the thing you
       * type into the app at the machine, and several lots SHARE one — V, Y,
       * F, Robson and Freedman are all 91520 — so it cannot be worked out from
       * the lot name. 11 of the priced areas have no zone at all (the parkade
       * and the oversized bays take payment another way), and those must show
       * nothing rather than a dead link.
       */
      zone: lot.zone ?? null,
      payLink: lot.payLink ?? null,
      /**
       * The schedule itself, not just the badge derived from it.
       *
       * A caller that wants to say WHEN a lot charges — rather than whether it
       * is charging this minute — has no way back to it otherwise, and
       * re-deriving the type from the badge text is guesswork.
       */
      paidSchedule: lot.paidSchedule ?? null,
      timeLimit: lot.timeLimit ?? null,
      dailyMax: lot.dailyMax ?? null,
    });
  }
  return out;
}
